import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Typography,
  Button,
  useTheme,
  Switch,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import GuideOpenButton from "../../components/GuideOpenButton";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowEditorReactFlow from "../../components/FlowBuilder/FlowEditorReactFlow";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";
import { useTimezone } from "../../hooks/useTimezone";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    padding: theme.spacing(1, 2),
  },
}));

const FlowBuilders = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { formatDate } = useTimezone();

  const [loading, setLoading] = useState(false);
  const [flows, setFlows] = useState([]);
  const [flowsCount, setFlowsCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingFlow, setDeletingFlow] = useState(null);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const [togglingFlowId, setTogglingFlowId] = useState(null);
  const [viewMode, setViewMode] = useViewMode("flowBuilders");
  const isMountedRef = useRef(true);

  // ✅ Cleanup para prevenir actualizaciones en componente desmontado
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParam(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const isNewFlow = id === "new" || location.pathname === "/flowbuilders/new";

  useEffect(() => {
    const path = location.pathname;
    const isEditorPath = path === "/flowbuilders/new" || (path.includes("/flowbuilders/") && path.includes("/edit"));
    const isNewPath = path === "/flowbuilders/new";
    
    if (isEditorPath && !isNewPath && id) {
      fetchFlowById();
    } else if (isNewPath) {
      setCurrentFlow({ name: "", flow: { nodes: [], edges: [] } });
    } else if (!isEditorPath) {
      fetchFlows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, id, searchParam, page, rowsPerPage]);

  const fetchFlowById = async () => {
    try {
      if (isMountedRef.current) {
        setLoadingFlow(true);
      }
      const { data } = await api.get(`/flowbuilders/${id}`);
      if (isMountedRef.current) {
        setCurrentFlow(data);
      }
    } catch (err) {
      if (isMountedRef.current) {
        toastError(err);
        history.push("/flowbuilders");
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingFlow(false);
      }
    }
  };

  const fetchFlows = async () => {
    try {
      if (isMountedRef.current) {
        setLoading(true);
      }
      const { data } = await api.get("/flowbuilders", {
        params: {
          searchParam: searchParam || "",
          pageNumber: page + 1,
          rowsPerPage,
        },
      });

      if (!isMountedRef.current) return;

      // ✅ Validar y mapear datos recibidos
      const flowsData = data.records || [];
      
      // ✅ Asegurar que todos los registros tengan ID válido y datos correctos
      const validFlows = flowsData.map((flow) => ({
        ...flow,
        id: flow?.id || flow?.Id || 0,
        name: flow?.name || flow?.Name || 'Sin nombre',
        active: flow?.active !== undefined ? flow.active : flow?.Active !== undefined ? flow.Active : false,
        createdAt: flow?.createdAt || flow?.CreatedAt || new Date()
      }));
      
      if (isMountedRef.current) {
        setFlows(validFlows);
        setFlowsCount(data.count || validFlows.length);
      }
    } catch (err) {
      if (isMountedRef.current) {
        toastError(err);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (event) => {
    setSearchInput(event.target.value);
  };

  const handleNewFlow = () => {
    history.push("/flowbuilders/new");
  };

  const handleEditFlow = (flow) => {
    if (!flow || !flow.id) {
      toast.error("Error: No se pudo obtener el ID del flujo");
      return;
    }
    history.push(`/flowbuilders/${flow.id}/edit`);
  };

  const handleDeleteFlow = async () => {
    if (!deletingFlow || !deletingFlow.id) {
      toast.error("Error: No se pudo obtener el ID del flujo");
      return;
    }

    try {
      await api.delete(`/flowbuilders/${deletingFlow.id}`);
      toast.success(i18n.t("flowbuilders.toasts.deleted"));
      fetchFlows();
      setConfirmModalOpen(false);
      setDeletingFlow(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleActive = async (flow) => {
    if (!flow?.id || togglingFlowId) return;
    try {
      setTogglingFlowId(flow.id);
      await api.put(`/flowbuilders/${flow.id}`, { active: !flow.active });
      toast.success(
        flow.active
          ? i18n.t("flowbuilders.toasts.deactivated") || "Flujo desactivado"
          : i18n.t("flowbuilders.toasts.activated") || "Flujo activado"
      );
      await fetchFlows();
    } catch (err) {
      toastError(err);
    } finally {
      setTogglingFlowId(null);
    }
  };

  const handleSaveFlow = async (flowData) => {
    try {
      if (isNewFlow) {
        const { data } = await api.post("/flowbuilders", {
          name: flowData.name || "Nuevo Flujo",
          flow: flowData.flow,
          active: true,
        });
        if (data?.id) {
          history.replace(`/flowbuilders/${data.id}/edit`);
          setCurrentFlow(data);
        }
      } else {
        const { data } = await api.put(`/flowbuilders/${id}`, {
          name: flowData.name,
          flow: flowData.flow,
        });
        if (data) {
          setCurrentFlow(data);
        }
      }
    } catch (err) {
      toastError(err);
      throw err;
    }
  };

  const handleCancelEditor = () => {
    history.push("/flowbuilders");
  };

  // ✅ Si es editor, mostrar el editor visual
  const path = location.pathname;
  const shouldShowEditor = path === "/flowbuilders/new" || (path.includes("/flowbuilders/") && path.includes("/edit"));
  const isNewPath = path === "/flowbuilders/new";
  
  if (shouldShowEditor) {
    if (loadingFlow && !isNewPath && id) {
      return (
        <MainContainer>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography>{i18n.t("flowbuilders.loading")}</Typography>
          </Box>
        </MainContainer>
      );
    }

    return (
      <MainContainer>
        <FlowEditorReactFlow
          flowId={isNewPath ? null : id}
          initialFlow={currentFlow || (isNewPath ? { name: "", flow: { nodes: [], edges: [] } } : null)}
          onSave={handleSaveFlow}
          onCancel={handleCancelEditor}
        />
      </MainContainer>
    );
  }

  // ✅ Si no es editor, mostrar lista
  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {i18n.t("flowbuilders.title")}
            <GuideOpenButton id="flows" title={i18n.t("flowbuilders.title")} to="/flowbuilders" startId="start" />
          </span>
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewFlow}
            startIcon={<AddIcon />}
          >
            {i18n.t("flowbuilders.buttons.add")}
          </Button>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <TextField
            placeholder={i18n.t("flowbuilders.searchPlaceholder")}
            type="search"
            size="small"
            variant="outlined"
            value={searchInput}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {flows.map((flow, index) => (
              <RecordCard
                key={`flow-card-${flow?.id || index}`}
                title={flow?.name || "Sin nombre"}
                actions={
                  <>
                    <IconButton size="small" onClick={() => handleEditFlow(flow)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setDeletingFlow(flow); setConfirmModalOpen(true); }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <RecordCardMeta>
                  {flow?.active ? i18n.t("flowbuilders.status.active") : i18n.t("flowbuilders.status.inactive")}
                </RecordCardMeta>
                <RecordCardMeta>{flow?.createdAt ? formatDate(flow.createdAt) : "N/A"}</RecordCardMeta>
              </RecordCard>
            ))}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.name")}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.status")}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.createdAt")}
              </TableCell>
              <TableCell className={classes.customTableCell} align="center">
                {i18n.t("flowbuilders.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={4} />}
            {!loading && flows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("flowbuilders.noFlows")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              flows.map((flow, index) => (
                <TableRow key={flow?.id || `flow-${index}`} hover>
                  <TableCell 
                    className={classes.customTableCell}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleEditFlow(flow)}
                  >
                    <Typography 
                      variant="body2" 
                      style={{ 
                        fontWeight: 500,
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                        textDecorationColor: 'transparent',
                        transition: 'text-decoration-color 0.2s',
                        '&:hover': {
                          textDecorationColor: theme.palette.primary.main,
                        }
                      }}
                    >
                      {flow?.name || 'Sin nombre'}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    <Box display="flex" alignItems="center">
                      <Switch
                        size="small"
                        color="primary"
                        checked={Boolean(flow?.active)}
                        disabled={togglingFlowId === flow?.id}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggleActive(flow)}
                      />
                      <Chip
                        label={flow?.active ? i18n.t("flowbuilders.status.active") : i18n.t("flowbuilders.status.inactive")}
                        color={flow?.active ? "primary" : "default"}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    {flow?.createdAt ? formatDate(flow.createdAt) : 'N/A'}
                  </TableCell>
                  <TableCell className={classes.customTableCell} align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditFlow(flow)}
                        style={{ color: theme.palette.primary.main }}
                        title={i18n.t("flowbuilders.actions.edit") || "Editar"}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingFlow(flow);
                          setConfirmModalOpen(true);
                        }}
                        style={{ color: theme.palette.error.main }}
                        title={i18n.t("flowbuilders.actions.delete") || "Eliminar"}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        )}
        <TablePagination
          component="div"
          count={flowsCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          labelRowsPerPage={i18n.t("flowbuilders.rowsPerPage")}
        />
        <ConfirmationModal
          title={i18n.t("flowbuilders.confirmationModal.deleteTitle")}
          open={confirmModalOpen}
          onClose={() => {
            setConfirmModalOpen(false);
            setDeletingFlow(null);
          }}
          onConfirm={handleDeleteFlow}
        >
          {i18n.t("flowbuilders.confirmationModal.deleteMessage")}
        </ConfirmationModal>
      </Paper>
    </MainContainer>
  );
};

export default FlowBuilders;
